package com.karyadina.ui.components

import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Help
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.karyadina.model.ScanMode

/**
 * CompositionLocal for providing platform-specific camera implementation
 */
val LocalCameraProvider = compositionLocalOf<(@Composable (ScanMode, (String?, String?) -> Unit, () -> Unit) -> Unit)?> { null }

/**
 * Camera scan bottom sheet for capturing and extracting text
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CameraScanBottomSheet(
    onDismiss: () -> Unit,
    onScanComplete: (scanMode: ScanMode, extractedText: String?, imagePath: String?) -> Unit,
    modifier: Modifier = Modifier
) {
    var selectedMode by remember { mutableStateOf(ScanMode.QUESTION) }
    var showCameraView by remember { mutableStateOf(false) }
    
    // When camera is showing, render only the camera (hide bottom sheet)
    if (showCameraView) {
        CameraViewPlaceholder(
            scanMode = selectedMode,
            onCapture = { text, path ->
                showCameraView = false
                onScanComplete(selectedMode, text, path)
            },
            onDismiss = { 
                showCameraView = false
                // Don't call onDismiss here, go back to bottom sheet
            }
        )
        return // Exit early, don't render bottom sheet
    }
    
    // Bottom sheet UI (only shown when camera is not active)
    ModalBottomSheet(
        onDismissRequest = onDismiss,
        modifier = modifier
    ) {
        androidx.compose.foundation.lazy.LazyColumn(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 24.dp),
            contentPadding = PaddingValues(bottom = 24.dp, top = 8.dp),
            verticalArrangement = Arrangement.spacedBy(20.dp)
        ) {
            // Header
            item {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column {
                        Text(
                            "Scan & Extract",
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold
                        )
                        Text(
                            "Capture text from your notes",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
                        )
                    }
                    IconButton(onClick = onDismiss) {
                        Icon(Icons.Default.Close, "Close")
                    }
                }
            }
            
            // Scan Mode Selection
            item {
                Text(
                    "What do you want to scan?",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Medium
                )
            }
            
            item {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                ScanModeCard(
                    title = "Question Text",
                    description = "Extract text for question field",
                    icon = Icons.AutoMirrored.Filled.Help,
                    color = Color(0xFF2196F3),
                    selected = selectedMode == ScanMode.QUESTION,
                    onClick = { selectedMode = ScanMode.QUESTION }
                )
                
                ScanModeCard(
                    title = "Answer Text",
                    description = "Extract text for answer field",
                    icon = Icons.Default.CheckCircle,
                    color = Color(0xFF4CAF50),
                    selected = selectedMode == ScanMode.ANSWER,
                    onClick = { selectedMode = ScanMode.ANSWER }
                )
                
                ScanModeCard(
                    title = "Image Note",
                    description = "Save as image without text extraction",
                    icon = Icons.Default.Image,
                    color = Color(0xFFFF9800),
                    selected = selectedMode == ScanMode.IMAGE_ONLY,
                    onClick = { selectedMode = ScanMode.IMAGE_ONLY }
                )
                
                ScanModeCard(
                    title = "Formula/Equation",
                    description = "Scan mathematical formula with recognition",
                    icon = Icons.Default.Functions,
                    color = Color(0xFF9C27B0),
                    selected = selectedMode == ScanMode.FORMULA,
                    onClick = { selectedMode = ScanMode.FORMULA }
                )
            }
            
            // Action Buttons
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                OutlinedButton(
                    onClick = onDismiss,
                    modifier = Modifier.weight(1f)
                ) {
                    Text("Cancel")
                }
                
                Button(
                    onClick = { showCameraView = true },
                    modifier = Modifier.weight(1f)
                ) {
                    Icon(Icons.Default.CameraAlt, contentDescription = null, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Open Camera")
                }
            }
            
            // Info Card
            Surface(
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(12.dp),
                color = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.3f)
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(16.dp),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalAlignment = Alignment.Top
                ) {
                    Icon(
                        Icons.Default.Info,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.size(20.dp)
                    )
                    Column(
                        modifier = Modifier.weight(1f),
                        verticalArrangement = Arrangement.spacedBy(4.dp)
                    ) {
                        Text(
                            "Tips for better scanning:",
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.primary
                        )
                        Text(
                            "• Use good lighting",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                        )
                        Text(
                            "• Keep text clear and straight",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                        )
                        Text(
                            "• Avoid shadows and glare",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                        )
                    }
                }
            }
            }
        }
    }
}

/**
 * Optional: Provide Android-specific camera via composition
 * This allows android module to inject AndroidCameraView
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CameraScanBottomSheetWithAndroidCamera(
    onDismiss: () -> Unit,
    onScanComplete: (scanMode: ScanMode, extractedText: String?, imagePath: String?) -> Unit,
    androidCameraView: @Composable ((scanMode: ScanMode, onCapture: (String?, String?) -> Unit, onDismiss: () -> Unit) -> Unit)? = null,
    modifier: Modifier = Modifier
) {
    var selectedMode by remember { mutableStateOf(ScanMode.QUESTION) }
    var showCameraView by remember { mutableStateOf(false) }
    
    ModalBottomSheet(
        onDismissRequest = onDismiss,
        modifier = modifier
    ) {
        androidx.compose.foundation.lazy.LazyColumn(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 24.dp),
            contentPadding = PaddingValues(bottom = 24.dp, top = 8.dp),
            verticalArrangement = Arrangement.spacedBy(20.dp)
        ) {
            // Header
            item {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column {
                    Text(
                        "Scan & Extract",
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        "Capture text from your notes",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
                    )
                }
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Default.Close, "Close")
                }
            }
            }
            
            // Scan Mode Selection
            item {
                Text(
                    "What do you want to scan?",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Medium
                )
            }
            
            item {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                ScanModeCard(
                    title = "Question Text",
                    description = "Extract text for question field",
                    icon = Icons.AutoMirrored.Filled.Help,
                    color = Color(0xFF2196F3),
                    selected = selectedMode == ScanMode.QUESTION,
                    onClick = { selectedMode = ScanMode.QUESTION }
                )
                
                ScanModeCard(
                    title = "Answer Text",
                    description = "Extract text for answer field",
                    icon = Icons.Default.CheckCircle,
                    color = Color(0xFF4CAF50),
                    selected = selectedMode == ScanMode.ANSWER,
                    onClick = { selectedMode = ScanMode.ANSWER }
                )
                
                ScanModeCard(
                    title = "Image Note",
                    description = "Save as image without text extraction",
                    icon = Icons.Default.Image,
                    color = Color(0xFFFF9800),
                    selected = selectedMode == ScanMode.IMAGE_ONLY,
                    onClick = { selectedMode = ScanMode.IMAGE_ONLY }
                )
                
                ScanModeCard(
                    title = "Formula/Equation",
                    description = "Scan mathematical formula with recognition",
                    icon = Icons.Default.Functions,
                    color = Color(0xFF9C27B0),
                    selected = selectedMode == ScanMode.FORMULA,
                    onClick = { selectedMode = ScanMode.FORMULA }
                )
            }
            }
            
            // Action Buttons
            item {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    OutlinedButton(
                        onClick = onDismiss,
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("Cancel")
                    }
                    
                    Button(
                        onClick = { showCameraView = true },
                        modifier = Modifier.weight(1f)
                    ) {
                        Icon(Icons.Default.CameraAlt, contentDescription = null, modifier = Modifier.size(18.dp))
                        Spacer(Modifier.width(8.dp))
                        Text("Open Camera")
                    }
                }
            }
            
            // Info Card
            item {
                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    color = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.3f)
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalAlignment = Alignment.Top
                    ) {
                        Icon(
                            Icons.Default.Info,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.primary,
                            modifier = Modifier.size(20.dp)
                        )
                        Column(
                            modifier = Modifier.weight(1f),
                            verticalArrangement = Arrangement.spacedBy(4.dp)
                        ) {
                            Text(
                                "Tips for better scanning:",
                                style = MaterialTheme.typography.labelMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.primary
                            )
                            Text(
                                "• Use good lighting",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                            )
                            Text(
                                "• Keep text clear and straight",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                            )
                            Text(
                                "• Avoid shadows and glare",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                            )
                        }
                    }
                }
            }
        }
    }
    
    // Camera view - use provided Android camera or fall back to platform default
    if (showCameraView) {
        if (androidCameraView != null) {
            // Use injected Android camera
            androidCameraView(
                selectedMode,
                { text, path ->
                    showCameraView = false
                    onScanComplete(selectedMode, text, path)
                },
                { showCameraView = false }
            )
        } else {
            // Fall back to platform default
            CameraViewPlaceholder(
                scanMode = selectedMode,
                onCapture = { text, path ->
                    showCameraView = false
                    onScanComplete(selectedMode, text, path)
                },
                onDismiss = { showCameraView = false }
            )
        }
    }
}

@Composable
fun ScanModeCard(
    title: String,
    description: String,
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    color: Color,
    selected: Boolean,
    onClick: () -> Unit
) {
    Surface(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        color = if (selected) color.copy(alpha = 0.2f) else MaterialTheme.colorScheme.surface,
        border = if (selected) androidx.compose.foundation.BorderStroke(2.dp, color) else null,
        tonalElevation = if (selected) 0.dp else 2.dp
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Surface(
                shape = CircleShape,
                color = color.copy(alpha = 0.2f),
                modifier = Modifier.size(48.dp)
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(
                        icon,
                        contentDescription = null,
                        tint = color,
                        modifier = Modifier.size(24.dp)
                    )
                }
            }
            
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold
                )
                Text(
                    description,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
                )
            }
            
            if (selected) {
                Icon(
                    Icons.Default.CheckCircle,
                    contentDescription = "Selected",
                    tint = color,
                    modifier = Modifier.size(24.dp)
                )
            }
        }
    }
}

/**
 * Platform-specific camera implementation
 * Android: Full CameraX implementation with OCR
 * Other platforms: Placeholder with "not implemented" message
 */
@Composable
expect fun CameraViewPlaceholder(
    scanMode: ScanMode,
    onCapture: (text: String?, imagePath: String?) -> Unit,
    onDismiss: () -> Unit
)
